import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/articles';
import { usePageMeta } from '@/hooks/usePageMeta';

const anchors = [
  { id: 'intro', label: '网站介绍', icon: 'ri-information-line' },
  { id: 'contact', label: '联系方式', icon: 'ri-mail-line' },
  { id: 'usage', label: '使用说明', icon: 'ri-book-read-line' },
  { id: 'privacy', label: '隐私声明', icon: 'ri-shield-check-line' },
];

const socialLinks = [
  { icon: 'ri-wechat-line', label: '微信公众号：lualu-media' },
  { icon: 'ri-weibo-line', label: '微博：@鲁啊鲁内容平台' },
  { icon: 'ri-bilibili-line', label: '哔哩哔哩：鲁啊鲁' },
  { icon: 'ri-youtube-line', label: '视频号：鲁啊鲁' },
];

export default function About() {
  usePageMeta(
    '关于我们 - 鲁啊鲁多元内容平台',
    '了解鲁啊鲁的定位、内容范围、联系方式、使用说明与隐私声明，与我们一起构建多元内容社区。',
  );
  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <div className="mt-5 rounded-lg bg-primary-50 border border-primary-100 px-6 py-8 md:px-10 md:py-10">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">关于鲁啊鲁</h1>
        <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
          鲁啊鲁是一个面向广泛互联网用户的多元内容平台，聚合时事、科技、生活、文化与娱乐等领域资讯，
          并通过社区让阅读延伸为交流。我们相信，清晰的内容结构与真诚的互动，能让信息更有价值。
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-3">
            <p className="px-3 py-2 text-xs font-medium text-foreground-400">页面导航</p>
            {anchors.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-700 transition-colors"
              >
                <i className={item.icon}></i>
                {item.label}
              </a>
            ))}
          </div>
        </aside>

        <div className="lg:col-span-9 space-y-6">
          <section id="intro" className="scroll-mt-24 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              网站介绍
            </h2>
            <p className="mt-4 text-sm md:text-base leading-8 text-foreground-700">
              鲁啊鲁定位于多领域资讯与社区互动的综合内容平台，旨在帮助用户在信息繁杂的环境中，
              快速获取最新资讯、浏览专题内容，并与他人展开有价值的讨论。我们覆盖以下内容领域：
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  className="flex items-start gap-3 rounded-md border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                >
                  <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${cat.icon} text-lg`}></i>
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-900">{cat.name}</span>
                    <span className="block mt-0.5 text-xs leading-relaxed text-foreground-500">{cat.desc}</span>
                  </span>
                </Link>
              ))}
            </div>
            <p className="mt-5 text-sm md:text-base leading-8 text-foreground-700">
              未来，鲁啊鲁希望持续完善内容生态与社区体验，让每一位用户既能在这里找到需要的信息，
              也能留下自己的声音。
            </p>
          </section>

          <section id="contact" className="scroll-mt-24 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              联系方式
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              以下联系方式为示范内容，仅用于页面展示。
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 rounded-md bg-background-100 p-4">
                <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-400">合作邮箱</span>
                  <span className="block text-sm text-foreground-800">hello@lualu-demo.com</span>
                </span>
              </div>
              <div className="flex items-center gap-3 rounded-md bg-background-100 p-4">
                <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-400">联系电话（示范）</span>
                  <span className="block text-sm text-foreground-800">400-000-0000</span>
                </span>
              </div>
            </div>
            <div className="mt-4 space-y-2.5">
              {socialLinks.map((item) => (
                <div key={item.label} className="flex items-center gap-3 text-sm text-foreground-600">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                    <i className={`${item.icon} text-base`}></i>
                  </span>
                  {item.label}
                </div>
              ))}
            </div>
          </section>

          <section id="usage" className="scroll-mt-24 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              使用说明
            </h2>
            <ul className="mt-4 space-y-3 text-sm md:text-base leading-7 text-foreground-700">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-primary-500 mt-1"></i>
                通过顶部导航或分类入口浏览不同领域的内容，点击卡片即可进入详情页阅读。
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-primary-500 mt-1"></i>
                在详情页可收藏感兴趣的内容，收藏记录保存在你的浏览器本地，刷新后依然存在。
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-primary-500 mt-1"></i>
                使用搜索框查找关键词，可同时匹配文章与社区话题。
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-primary-500 mt-1"></i>
                社区板块当前为前端演示，发帖与回复不会保存到服务器。
              </li>
            </ul>
          </section>

          <section id="privacy" className="scroll-mt-24 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              隐私声明
            </h2>
            <p className="mt-4 text-sm md:text-base leading-8 text-foreground-700">
              鲁啊鲁高度重视用户隐私。当前版本为演示站点，
              <strong className="font-semibold text-foreground-900">所有内容标题、摘要、图片与社区信息均为虚构示范数据</strong>
              ，不代表任何真实作品或事件。
            </p>
            <p className="mt-3 text-sm md:text-base leading-8 text-foreground-700">
              站点不收集任何个人身份信息。收藏功能仅在你的浏览器本地存储中记录内容编号，
              不会上传至服务器，也不会被第三方获取。你可以随时通过清除浏览器数据移除这些记录。
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}